import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '@connect-services/client/environments/environment';
import {
    GoogleStreetViewService as GoogleStreetViewServiceSdk,
    License,
    Me,
    MeLicense,
} from '@connect-services/client/sdk/api';
import {
    ImmersiveViewErrorEnum,
    ImmersiveViewPosition,
    ImmersiveViewService,
    ImmersiveViewServiceEnum,
} from '@connect-services/client/services/immersive-view/immersive-view.service';
import { ImmersiveViewUiService } from '@connect-services/client/services/immersive-view/immersive-view-ui.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import StreetViewPanorama = google.maps.StreetViewPanorama;
import LatLng = google.maps.LatLng;
import StreetViewPanoramaData = google.maps.StreetViewPanoramaData;
import StreetViewStatus = google.maps.StreetViewStatus;

declare global {
    interface Window {
        google?: typeof google;
        initMapBootstrap: () => void;
    }
}

export interface StreetViewQuota {
    remaining: number;
    total: number;
}

const STREET_VIEW_SEARCH_RADIUS_METERS = 100;
const STREET_VIEW_DEBOUNCE_DURATION = 600;

@Injectable({
    providedIn: 'root',
})
export class GoogleStreetViewService {
    public readonly canRequestStreetView = computed<boolean>(() => {
        return (
            this.immersiveViewService.canSelectImmersiveView(ImmersiveViewServiceEnum.GOOGLE_STREET_VIEW) &&
            (this.organizationStreetViewQuota()?.remaining ?? 0) > 0 &&
            (this.userStreetViewQuota()?.remaining ?? 0) > 0
        );
    });
    public readonly currentPanorama = signal<google.maps.StreetViewPanorama | null>(null);
    public readonly noAvailableQuotaMessage = computed<string>(() => {
        const me: Me | null = this.securityService.meSignal();
        const license: MeLicense | undefined = this.license();

        if (!license) {
            return 'streetView.accessDeniedForAdminDoticUsers';
        }

        if (me?.organization.streetViewQuota === null) {
            return 'streetView.organizationHasNoQuotaSet';
        }

        if (!me?.organization.streetViewQuotaRemaining) {
            return 'streetView.organizationHasNoQuotaLeft';
        }

        if (!license.streetViewQuotaRemaining) {
            return 'streetView.accountHasNoQuotaLeft';
        }

        return 'immersiveView.viewerUnavailable';
    });
    public readonly organizationStreetViewQuota = computed<StreetViewQuota | null>(() => {
        const organization = this.securityService.meSignal()?.organization;

        if (!organization) {
            return null;
        }

        return {
            remaining: organization.streetViewQuotaRemaining ?? 0,
            total: organization.streetViewQuota ?? 0,
        };
    });

    public readonly userStreetViewQuota = computed<StreetViewQuota | null>(() => {
        const license: MeLicense | undefined = this.license();

        if (!license) {
            return null;
        }

        return {
            remaining: license.streetViewQuotaRemaining ?? 0,
            total: license.streetViewQuota ?? 0,
        };
    });

    private apiKey = environment.googleMapsApiKey;
    private container: HTMLElement | null = null;
    private debounceTimeout: ReturnType<typeof setTimeout> | null = null;
    private readonly license = computed<MeLicense | undefined>(() =>
        this.securityService
            .meSignal()
            ?.licenses.find((license: MeLicense) => license.application === License.ApplicationEnum.CONNECT_SERVICES),
    );
    private searchVersion = 0;
    private service: google.maps.StreetViewService | null = null;

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly googleStreetViewServiceSdk = inject(GoogleStreetViewServiceSdk);
    private readonly immersiveViewService = inject(ImmersiveViewService);
    private readonly immersiveViewUiService = inject(ImmersiveViewUiService);
    private readonly securityService = inject(SecurityService);

    public decrementQuota(lngLat: ImmersiveViewPosition, panoramaId: string): Promise<number> {
        return firstValueFrom(
            this.googleStreetViewServiceSdk.googleStreetViewControllerDecrementQuota({
                createRequestLogDto: { latitude: lngLat.lat, longitude: lngLat.lng, panoramaId },
            }),
        );
    }

    public findPanoramaForLocation(position: ImmersiveViewPosition): void {
        this.clearPendingSearch();

        const version = ++this.searchVersion;

        this.immersiveViewService.error.set(null);

        if (!this.canRequestStreetView()) {
            this.handleUnavailableStreetView();

            return;
        }

        if (!this.container || !this.service) {
            this.immersiveViewService.error.set(ImmersiveViewErrorEnum.VIEWER_UNAVAILABLE);
            this.immersiveViewService.isLoading.set(false);

            return;
        }

        this.immersiveViewService.isLoading.set(true);

        this.debounceTimeout = setTimeout(() => {
            this.debounceTimeout = null;
            this.requestPanorama(position, version);
        }, STREET_VIEW_DEBOUNCE_DURATION);
    }

    public async initGoogleMapsApi(): Promise<void> {
        if (this.service) {
            return;
        }

        if (window.google?.maps) {
            this.service = new google.maps.StreetViewService();

            return;
        }

        const mapsInitPromise = new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            const params = new URLSearchParams({
                callback: 'initMapBootstrap',
                key: this.apiKey,
                v: 'weekly',
            });

            script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
            script.async = true;
            script.defer = true;
            script.onerror = (): void => reject(new Error('Failed to load Google Maps script'));
            document.head.appendChild(script);

            window.initMapBootstrap = (): void => {
                resolve();
            };
        });

        await mapsInitPromise;

        if (!this.service) {
            this.service = new google.maps.StreetViewService();
        }
    }

    public resetStreetView(): void {
        this.searchVersion++;

        this.clearPendingSearch();

        const panorama: StreetViewPanorama | null = this.currentPanorama();

        if (panorama) {
            google.maps.event.clearInstanceListeners(panorama);
        }

        this.currentPanorama.set(null);
        this.container?.replaceChildren();
        this.container = null;
    }

    public setContainer(container: HTMLElement): void {
        this.container = container;
    }

    private clearPendingSearch(): void {
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
            this.debounceTimeout = null;
        }
    }

    private handlePanoramaResult(
        result: google.maps.StreetViewPanoramaData | null,
        status: google.maps.StreetViewStatus,
        position: ImmersiveViewPosition,
        version: number,
    ): void {
        if (!this.isCurrentSearch(version, position)) {
            return;
        }

        if (!this.canRequestStreetView()) {
            this.handleUnavailableStreetView();

            return;
        }

        if (status !== google.maps.StreetViewStatus.OK || !result?.location?.latLng) {
            this.immersiveViewService.error.set(ImmersiveViewErrorEnum.PANORAMA_NOT_FOUND);
            this.immersiveViewService.isLoading.set(false);

            return;
        }

        try {
            const panorama: StreetViewPanorama | null = this.currentPanorama();

            if (!panorama) {
                this.initStreetView(result.location.latLng);
            } else if (result.location.pano && panorama.getPano() !== result.location.pano) {
                panorama.setPano(result.location.pano);
            }

            this.immersiveViewService.error.set(null);
        } catch {
            this.immersiveViewService.error.set(ImmersiveViewErrorEnum.VIEWER_UNAVAILABLE);
        } finally {
            this.immersiveViewService.isLoading.set(false);
        }
    }

    private handleUnavailableStreetView(): void {
        const hasPermission = this.immersiveViewService.immersiveViewPermissions().streetViewPermission;

        this.immersiveViewService.error.set(
            hasPermission ? ImmersiveViewErrorEnum.GOOGLE_QUOTA_EXHAUSTED : ImmersiveViewErrorEnum.ACCESS_DENIED,
        );

        this.immersiveViewService.isLoading.set(false);
    }

    private initStreetView(panoCoords: google.maps.LatLng): void {
        const container: HTMLElement | null = this.container;

        if (!container) {
            return;
        }

        const panorama = new google.maps.StreetViewPanorama(container, {
            fullscreenControl: false,
            position: panoCoords,
            pov: { heading: 0, pitch: 0 },
            zoom: 1,
        });

        this.currentPanorama.set(panorama);

        panorama.addListener('position_changed', async () => {
            const position: LatLng | null = panorama.getPosition();
            const markerPosition: ImmersiveViewPosition | null = this.immersiveViewService.markerPosition();

            if (!position || !markerPosition) {
                return;
            }

            const newPosition: ImmersiveViewPosition = position.toJSON();

            if (markerPosition.lat === newPosition.lat && markerPosition.lng === newPosition.lng) {
                return;
            }

            const panoramaId: string = panorama.getPano();

            this.immersiveViewService.markerPosition.set(newPosition);

            try {
                const quota: number = await this.decrementQuota(newPosition, panoramaId);

                await this.securityService.updateMe();

                if (quota) {
                    return;
                }

                panorama.setOptions({
                    clickToGo: false,
                    disableDefaultUI: true,
                });
            } catch {
                /* empty */
            }
        });
    }

    private isCurrentSearch(version: number, position: ImmersiveViewPosition): boolean {
        return (
            version === this.searchVersion &&
            this.immersiveViewService.isImmersiveViewActive() &&
            this.immersiveViewUiService.isPanelOpened() &&
            this.immersiveViewService.currentImmersiveView() === ImmersiveViewServiceEnum.GOOGLE_STREET_VIEW &&
            this.immersiveViewService.requestedPosition() === position
        );
    }

    private requestPanorama(position: ImmersiveViewPosition, version: number): void {
        if (!this.isCurrentSearch(version, position)) {
            return;
        }

        if (!this.canRequestStreetView()) {
            this.handleUnavailableStreetView();

            return;
        }

        try {
            this.service!.getPanorama(
                { location: position, radius: STREET_VIEW_SEARCH_RADIUS_METERS },
                (result: StreetViewPanoramaData | null, status: StreetViewStatus) => {
                    this.handlePanoramaResult(result, status, position, version);
                },
            );
        } catch {
            if (!this.isCurrentSearch(version, position)) {
                return;
            }

            this.immersiveViewService.error.set(ImmersiveViewErrorEnum.VIEWER_UNAVAILABLE);
            this.immersiveViewService.isLoading.set(false);
        }
    }
}
